import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { TrackerContext } from './context';
import { usePublicEnv, ContextType } from '@app/shared/contexts/PublicEnv';
import { useIntl } from 'react-intl';
import { FilterStates } from '@app/orders/infra/config/FilterStates';
import { initialize as initializeTracker, track } from '@pedidosya/web-native-bridges/tracker';
import type { FC, ReactNode } from 'react';
import { Constants } from '@app/shared/config/constants';
import isEmpty from 'lodash/isEmpty';
import Translations from '@app/translations/globalTranslations';
import GTMEvents, { GTMEventParams } from '@app/shared/config/gtmEvents';
import find from 'lodash/find';

type Props = {
  children: ReactNode;
  config?: any;
  tracker: { track(name: string, param: any, origin?: string): void } | null;
};

const TrackerProvider: FC<Props> = memo(function TrackerProvider({ children, tracker, config }) {
  const [trackerInstance, setTracker] = useState(tracker);
  const { userId } = usePublicEnv() as ContextType;
  const { formatMessage } = useIntl();

  const loadTracker = useCallback(async () => {
    try {
      await initializeTracker(config);
      setTracker({ track });
    } catch (e) {
      return null;
    }
  }, [config]);

  useEffect(() => {
    if (!isEmpty(trackerInstance)) {
      return;
    }

    loadTracker();
  }, [trackerInstance, loadTracker]);

  /**
   * applyFilter
   *
   * @description Function to fire event to gtm to track
   * when user make select some filter a apply them
   * @param {string} origin - Origin request
   * @param {stirng} filterStateValue - Filter state value
   * @param {string} filterPeriodValue - Filter perdiod value
   */
  const applyFilter = (origin: string, filterStateValue: string, filterPeriodValue: string) => {
    const values = {
      origin,
      userId,
    };

    if (filterPeriodValue) {
      (values as any)['filterPeriod'] = filterPeriodValue;
    }

    if (filterStateValue) {
      const pill = find(FilterStates, (element) => element.value === filterStateValue);
      (values as any)['filterState'] = pill
        ? formatMessage((Translations as any)[pill.translationKey])
        : '';
    }
  };
  /**
   * repeatClicked
   *
   * @description Function to fire event to gtm to track
   * when user make click over repeat order button
   * @param {string} origin - Origin request
   * @param {string} orderId - Order id
   * @param {string} position - Position or card
   */
  const repeatClicked = (origin: string, orderId: string, position: number) => {
    trackerInstance?.track(
      GTMEvents.RepeatClicked,
      {
        origin,
        userId,
        orderId,
        position,
      },
      Constants.ORIGIN,
    );
  };
  /**
   * swipedCards
   *
   * @description Function to fire event to gtm to track
   * when user make swipe on current orders
   * @param {string} origin - Origin request
   */
  const swipedCards = (origin: string) => {
    trackerInstance?.track(
      GTMEvents.SwipedCards,
      {
        origin,
        userId,
      },
      Constants.ORIGIN,
    );
  };

  const basketCouchmarkLoaded = (origin: string, basketId: string, vendorId: number) => {
    trackerInstance?.track(
      GTMEvents.BasketCouchmarkLoaded,
      {
        origin,
        userId,
        shopId: vendorId,
        cartGuid: basketId,
      },
      Constants.ORIGIN,
    );
  };

  const basketClicked = (origin: string, basketId: string, vendorId: number) => {
    trackerInstance?.track(
      GTMEvents.BasketClicked,
      {
        origin,
        userId,
        cartGuid: basketId,
        shopId: vendorId,
      },
      Constants.ORIGIN,
    );
  };

  /**
   * helpClicked
   *
   * @description Function to fire event to gtm track when user click over partner
   * @param {string} origin - Origin request
   * @param {string} orderId - order Id
   * @param {string} orderState - order state
   */
  const helpClicked = (origin: string, orderId: string, orderState: string = '') => {
    trackerInstance?.track(
      GTMEvents.HelpClicked,
      {
        eventOrigin: origin,
        userId,
        orderId,
        orderState,
        screenName: GTMEventParams.SCREEN_NAME,
        rechargeTo: GTMEventParams.RECHARGE_TO,
      },
      Constants.ORIGIN,
    );
  };

  /**
   * pageLoaded
   *
   * @description Function to fire event to gtm track when page is loaded
   * @param {string} origin - Origin request
   */
  const pageLoaded = (
    origin: string,
    qtyOrders: number = 0,
    qtyLiveOrders: number = 0,
    qtyCancelledOrders: number = 0,
    qtyDeliveredOrders: number = 0,
  ) => {
    const params = {
      userId,
      eventOrigin: origin,
      qtyOrders,
      qtyLiveOrders,
      qtyCancelledOrders_less_than_1h: qtyCancelledOrders,
      qtyDeliveredOrders_less_than_1h: qtyDeliveredOrders,
      screenName: GTMEventParams.SCREEN_NAME,
      rechargeTo: GTMEventParams.RECHARGE_TO,
    };
    trackerInstance?.track(GTMEvents.LoadedPage, params, Constants.ORIGIN);
  };

  const value = useMemo(
    () => ({
      gtmTrack: trackerInstance?.track,
      applyFilter,
      swipedCards,
      repeatClicked,
      basketCouchmarkLoaded,
      basketClicked,
      helpClicked,
      pageLoaded,
      tracker: trackerInstance,
    }),
    [trackerInstance],
  );

  return <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>;
});

export { TrackerProvider };
